import ErrorFallback from '@/components/ErrorFallback';
import { useSubmitForMedicalRecordsConsent } from '@/hooks/useSubmitForMedicalRecordsConsent';
import { ControlledSignaturePad } from '@/pages/components/ControlledSignaturePad';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Container,
  Link,
  Stack,
  TextField,
  Typography,
} from '@montugroup/design-system';
import { ALT_CUSTOMER_SERVICE_NUMBER_DISPLAY, ALT_CUSTOMER_SERVICE_NUMBER_INTL } from '@montugroup/frontend-common';
import { useForm } from 'react-hook-form';
import { useSearchParams } from 'react-router-dom';
import { z } from 'zod';
import { MobileOnlyLogo, WithPageWrapper } from './components/Branding';

const ConsentMedicalRecordsSchema = z.object({
  healthPractitionerClinicName: z.string().min(1, 'Health Practitioner Clinic name is required'),
  healthPractitionerName: z.string().min(1, 'Health Practitioner name is required'),
  signature: z.string().min(1, 'Signature is required'),
});

type ConsentMedicalRecordsFormValues = z.infer<typeof ConsentMedicalRecordsSchema>;

export const ConsentMedicalRecords = () => {
  const [searchParams] = useSearchParams();
  const { mutateAsync, isSuccess, isPending, isError } = useSubmitForMedicalRecordsConsent();
  const paramData = searchParams.get('data');
  const rawParamKind = searchParams.get('kind');
  const paramKind = rawParamKind === 'patient-code' || rawParamKind === 'consent-uuid' ? rawParamKind : undefined; // for typing purposes

  const {
    control,
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<ConsentMedicalRecordsFormValues>({
    resolver: zodResolver(ConsentMedicalRecordsSchema),
    defaultValues: {
      healthPractitionerClinicName: '',
      healthPractitionerName: '',
      signature: '',
    },
  });

  // If no 'data' in url params, display a 404 error message
  if (!paramData) {
    return (
      <Container maxWidth="md" sx={{ paddingTop: 25 }}>
        <ErrorFallback />;
      </Container>
    );
  }

  const onSubmit = async (formData: ConsentMedicalRecordsFormValues) => {
    const { healthPractitionerClinicName, healthPractitionerName, signature } = formData;

    await mutateAsync({
      healthPractitionerClinicName,
      healthPractitionerName,
      signature,
      paramData,
      paramKind,
    });
  };

  const signatureDisclaimer = (
    <Typography sx={{ marginBottom: 4 }}>
      <strong>Important:</strong> Please ensure your signature is clear and legible, as it will be sent to your doctor.
      If the signature does not appear genuine, you may be opted out of this service. If you make a mistake, you can
      easily reset your signature and try again.
    </Typography>
  );

  const clearSignatureText = 'Reset your signature and try again';

  return WithPageWrapper(
    <Box
      sx={{
        minHeight: '100vh',
        display: 'grid',
        placeItems: 'center',
      }}
    >
      <Stack
        component="form"
        onSubmit={handleSubmit(onSubmit)}
        sx={{
          maxWidth: 700,
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
          px: { xs: 8, sm: 0 },
          pb: 15,
        }}
      >
        <Box sx={(theme) => ({ marginBottom: theme.spacing(4) })}>
          <MobileOnlyLogo />
        </Box>

        <Typography variant={'h5'} sx={{ marginBottom: 2 }}>
          Request for Records
        </Typography>
        <Typography variant={'body1'}>
          We are committed to protecting your privacy and know it is important for you to understand how your
          information is handled. Refer to our Montu Group{' '}
          <Link href="https://www.alternaleaf.com.au/privacy" target="_blank" rel="noopener noreferrer" color="primary">
            Privacy Policy
          </Link>{' '}
          and{' '}
          <Link
            href="https://www.alternaleaf.com.au/terms-of-service"
            target="_blank"
            rel="noopener noreferrer"
            color="primary"
          >
            Terms of Service
          </Link>{' '}
          for further information.
        </Typography>
        <Typography fontWeight={'bold'}>
          Please share your health practitioner details that you would like us to request records from.
        </Typography>
        <Typography fontStyle={'italic'}>
          Records could include: Health Care Summary, Letters of Support, Clinical Records, Discharge Letters, Pathology
          Reports.
        </Typography>
        <Typography fontStyle={'italic'}>All fields marked with an asterisk (*) are required.</Typography>

        <Box sx={{ display: 'flex', gap: 2, mt: 2, flexDirection: { xs: 'column', sm: 'row' } }}>
          {/* Health Practitioner Clinic Name */}
          <TextField
            label="Name of your health practitioner clinic *"
            {...register('healthPractitionerClinicName')}
            error={Boolean(errors.healthPractitionerClinicName)}
            helperText={errors.healthPractitionerClinicName?.message}
            sx={{ flex: 1 }}
          />

          {/* Health Practitioner Name */}
          <TextField
            label="Name of your health practitioner *"
            {...register('healthPractitionerName')}
            error={Boolean(errors.healthPractitionerName)}
            helperText={errors.healthPractitionerName?.message}
            sx={{ flex: 1 }}
          />
        </Box>

        {/* Signature Pad */}
        <Box sx={{ marginTop: 4 }}>
          <ControlledSignaturePad
            control={control}
            fieldName="signature"
            label="Signature *"
            signatureDisclaimer={signatureDisclaimer}
            clearSignatureText={clearSignatureText}
          />
        </Box>

        <Typography>
          Please sign within the box above to confirm that you consent to Alternaleaf requesting your health information
          and records from the above practitioner and clinic for the purpose of providing you with health services, and
          that you consent to the above practitioner and clinic providing this to Alternaleaf.
        </Typography>

        {isError && (
          <Box sx={{ my: 5 }}>
            <Alert severity="error">
              An error has occurred sending consent. Please try again or contact{' '}
              <Link href={`tel:${ALT_CUSTOMER_SERVICE_NUMBER_INTL}`}>{ALT_CUSTOMER_SERVICE_NUMBER_DISPLAY}</Link> for
              more help.
            </Alert>
          </Box>
        )}

        {isSuccess && (
          <Box sx={{ my: 5 }}>
            <Alert severity="success">Your consent has been successfully submitted.</Alert>
          </Box>
        )}

        <Button
          type="submit"
          variant="contained"
          size="large"
          sx={{ marginTop: 2 }}
          disabled={isPending}
          startIcon={isPending ? <CircularProgress size={16} /> : null}
        >
          Submit
        </Button>
      </Stack>
    </Box>,
    true,
  );
};
